import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import ProjectModal from '../components/ProjectModal';
import {
  Monitor,
  MessageSquare,
  MessageCircle,
  Settings,
  TrendingUp,
  Zap,
  DollarSign,
  Smartphone,
  Headphones,
  Mail,
  Phone,
  Send,
  Maximize2,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const Home = () => {
  const { toast } = useToast();
  const [formStatus, setFormStatus] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Logo and projects with actual uploaded images
  const logoUrl = 'https://customer-assets.emergentagent.com/job_jyoti-agency/artifacts/299sy1av_Logo%20%282%29.png';
  const projects = [
    { id: 1, image: 'https://customer-assets.emergentagent.com/job_jyoti-agency/artifacts/x2hk14ok_VELVET%20BREW%20CAFE%20WEBSITE.png', alt: 'Velvet Brew Cafe Website' },
    { id: 2, image: 'https://customer-assets.emergentagent.com/job_jyoti-agency/artifacts/pqqdhd3i_SPARKLE%20CLEANING%20WEBSITE.png', alt: 'Sparkle Cleaning Website' }
  ];

  const testimonials = [
    {
      id: 1,
      name: "Sarah Mitchell",
      company: "Velvet Brew Cafe",
      text: "Jyoti Web Design transformed our online presence completely. The website is beautiful, fast, and our online orders have increased by 150%!",
      rating: 5
    },
    {
      id: 2,
      name: "James Peterson",
      company: "SparklePro Cleaning",
      text: "Professional, creative, and delivered exactly what we needed. Our customer enquiries doubled within the first month of launching!",
      rating: 5
    },
    {
      id: 3,
      name: "Emily Chen",
      company: "TechStart Solutions",
      text: "Outstanding work! They understood our vision and created a website that perfectly represents our brand. Highly recommend!",
      rating: 5
    }
  ];

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    
    try {
      const response = await fetch('https://formspree.io/f/mvzdkjvw', {
        method: 'POST',
        body: new FormData(form),
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setFormStatus('success');
        toast({
          title: "Message Sent Successfully!",
          description: "Thanks! We will contact you soon.",
        });
        form.reset();
        
        // Clear success message after 5 seconds
        setTimeout(() => setFormStatus(''), 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setFormStatus('error');
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact us directly.",
        variant: "destructive",
      });
    }
  };

  const openProjectModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeProjectModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const services = [
    {
      icon: <Monitor className="w-10 h-10 text-purple-600" />,
      title: "Website Design & Digital Presence",
      description: "We create modern, high-performing websites that represent your brand and drive results.",
      points: [
        "Custom website design",
        "Mobile-responsive development",
        "SEO-ready structure",
        "Business landing pages",
        "Clean and user-friendly design"
      ]
    },
    {
      icon: <MessageSquare className="w-10 h-10 text-blue-600" />,
      title: "Customer Engagement Systems",
      description: "Connect with your customers instantly and never miss a potential enquiry.",
      points: [
        "Live chat assistant",
        "WhatsApp chat integration",
        "Smart enquiry handling",
        "Multi-channel communication setup",
        "Instant response system"
      ]
    },
    {
      icon: <Settings className="w-10 h-10 text-orange-600" />,
      title: "Business Automation Systems",
      description: "Automate your business processes and ensure smooth operations 24/7.",
      points: [
        "Missed call capture system",
        "Smart booking & scheduling automation",
        "Instant lead notifications",
        "Automated follow-ups (SMS / WhatsApp / email)",
        "Review request automation"
      ]
    },
    {
      icon: <TrendingUp className="w-10 h-10 text-green-600" />,
      title: "Growth & Marketing Systems",
      description: "Tools and systems designed to help your business grow and convert more customers.",
      points: [
        "Lead capture funnels",
        "Sales tracking system",
        "Social media integration",
        "Conversion optimisation",
        "Performance analytics setup"
      ]
    }
  ];

  const whyChooseUs = [
    { icon: <Zap className="w-10 h-10 text-yellow-600" />, title: "Fast Delivery", description: "Quick turnaround without compromising quality" },
    { icon: <DollarSign className="w-10 h-10 text-yellow-600" />, title: "Affordable Pricing", description: "Premium quality at competitive rates" },
    { icon: <Smartphone className="w-10 h-10 text-yellow-600" />, title: "Mobile-Friendly Designs", description: "Responsive across all devices" },
    { icon: <Headphones className="w-10 h-10 text-yellow-600" />, title: "Customer Support", description: "Dedicated support whenever you need" }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Logo Focused */}
      <section className="hero-section min-h-screen flex flex-col items-center justify-center px-4 py-20">
        <div className="relative z-10 max-w-2xl w-full text-center">
          {/* Logo */}
          <div className="mb-8">
            <div className="bg-white rounded-3xl p-12 shadow-xl transition-all duration-300 border-4 border-white">
              <img
                src={logoUrl}
                alt="Jyoti Web Design Logo"
                className="max-h-64 mx-auto object-contain"
              />
            </div>
          </div>

          {/* Creative Text Under Logo */}
          <div className="mb-8">
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-2 leading-tight tracking-tight">
              Designing Digital Experiences<br />That Light Up Your Brand
            </h1>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              View Services
            </Button>
            <Button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-900 px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-with-grid py-20 px-4" style={{
        backgroundImage: `url('https://images.pexels.com/photos/1170412/pexels-photo-1170412.jpeg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-4 drop-shadow-lg">Services We Provide</h2>
            <p className="text-xl text-white font-medium drop-shadow-md">Complete digital solutions for your business growth</p>
          </div>
          
          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-purple-200"
              >
                {/* Icon */}
                <div className="mb-6 bg-gradient-to-br from-gray-50 to-gray-100 w-16 h-16 rounded-xl flex items-center justify-center">
                  {service.icon}
                </div>
                
                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                
                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                
                {/* Bullet Points */}
                <ul className="space-y-3">
                  {service.points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-12 text-center max-w-5xl mx-auto shadow-2xl">
            <p className="text-white text-2xl md:text-3xl font-semibold leading-relaxed mb-8">
              We don't just build websites — we create complete digital systems that help your business attract, engage, and convert customers automatically.
            </p>
            <Button
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-purple-600 hover:bg-gray-100 px-10 py-7 text-xl rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-bold inline-flex items-center gap-3"
            >
              Get Started
              <ArrowRight className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-24 px-4 overflow-hidden">
        {/* Abstract Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50"></div>
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Text Content with Slide-in Animation */}
            <div className="space-y-8 animate-fadeInLeft">
              {/* Small Badge */}
              <div className="inline-block">
                <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg">
                  Who We Are
                </span>
              </div>

              {/* Heading */}
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                About <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Jyoti Web Design</span>
              </h2>

              {/* Description */}
              <div className="space-y-6">
                <p className="text-xl text-gray-700 leading-relaxed">
                  At Jyoti Web Design, we create modern, high-performing websites designed to help businesses grow. Our focus goes beyond design — we build smart digital systems that help you attract customers, respond instantly, and manage your business more efficiently.
                </p>
                <p className="text-xl text-gray-700 leading-relaxed">
                  From website design to customer engagement and business automation, we ensure your brand stays active, responsive, and ready to convert opportunities at all times.
                </p>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-6 pt-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Modern Design</p>
                      <p className="text-sm text-gray-600">Future-ready websites</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center shadow-lg">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Smart Systems</p>
                      <p className="text-sm text-gray-600">Automated solutions</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="pt-4">
                <Button
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  Start Your Project
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>

            {/* Right Side - Visual Element with Fade-in Animation */}
            <div className="relative animate-fadeInRight">
              {/* Decorative Cards Stack */}
              <div className="relative">
                {/* Back Card */}
                <div className="absolute top-8 left-8 w-full h-96 bg-gradient-to-br from-purple-400 to-pink-400 rounded-3xl shadow-2xl transform rotate-3 opacity-20"></div>
                
                {/* Middle Card */}
                <div className="absolute top-4 left-4 w-full h-96 bg-gradient-to-br from-blue-400 to-purple-400 rounded-3xl shadow-2xl transform rotate-2 opacity-40"></div>
                
                {/* Front Card - Image */}
                <div className="relative w-full h-96 bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white">
                  <img
                    src="https://images.pexels.com/photos/29152435/pexels-photo-29152435.jpeg"
                    alt="Modern Workspace"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-900/20 via-transparent to-blue-900/20"></div>
                  
                  {/* Floating Stats Badge */}
                  <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">50+</p>
                        <p className="text-xs text-gray-600 font-medium">Projects</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">100%</p>
                        <p className="text-xs text-gray-600 font-medium">Satisfaction</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">24/7</p>
                        <p className="text-xs text-gray-600 font-medium">Support</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="section-with-grid py-20 px-4" style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1772037440731-c37818735b5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBncmFkaWVudHxlbnwwfHx8fDE3NzYxNjEwMjh8MA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Recent Projects</h2>
            <p className="text-xl text-gray-800 font-medium">Check out some of our latest premium work</p>
          </div>
          
          {/* Portfolio Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {projects.map((project) => (
              <div 
                key={project.id}
                onClick={() => openProjectModal(project)}
                className="group cursor-pointer"
              >
                <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
                  <div 
                    className="relative w-full overflow-hidden"
                    style={{ height: '350px' }}
                  >
                    <img
                      src={project.image}
                      alt={project.alt}
                      className="absolute inset-0 w-full h-full transition-transform duration-500 group-hover:scale-110"
                      style={{
                        objectFit: 'cover',
                        objectPosition: 'center',
                        minWidth: '100%',
                        minHeight: '100%'
                      }}
                    />
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white rounded-full p-4 shadow-2xl transform scale-0 group-hover:scale-100 transition-transform duration-300">
                          <Maximize2 className="w-8 h-8 text-gray-900" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Modal */}
      <ProjectModal 
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={closeProjectModal}
      />

      {/* Why Choose Us Section */}
      <section id="why-choose-us" className="section-with-grid py-20 px-4" style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1725688637928-c67974234323?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxjb2xvcmZ1bCUyMG5lb24lMjBsaWdodGluZ3xlbnwwfHx8fDE3NzYxNTk0NzZ8MA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
            <p className="text-xl text-gray-800 font-medium">What makes us stand out</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="bg-white hover:shadow-2xl transition-all duration-300 hover:scale-105 border-0 rounded-2xl">
                <CardContent className="p-8 text-center">
                  <div className="mb-6 flex justify-center">{item.icon}</div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                  <p className="text-gray-700">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Client Testimonials Section */}
      <section id="testimonials" className="section-with-grid py-20 px-4" style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1763356844645-dbfd9244fa20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHx2aWJyYW50JTIwd29ya3NwYWNlfGVufDB8fHx8MTc3NjE1OTQ3M3ww&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-800 font-medium">Real feedback from our satisfied customers</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white hover:shadow-2xl transition-all duration-300 hover:scale-105 border-0 rounded-2xl">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-yellow-500 text-2xl">★</span>
                    ))}
                  </div>
                  <p className="text-gray-700 text-lg mb-6 italic">"{testimonial.text}"</p>
                  <div className="border-t-2 border-gray-200 pt-4">
                    <p className="font-bold text-gray-900 text-lg">{testimonial.name}</p>
                    <p className="text-gray-600">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-with-grid py-20 px-4" style={{
        backgroundImage: `url('https://images.pexels.com/photos/7062225/pexels-photo-7062225.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="max-w-4xl mx-auto relative z-10">
          <Card className="bg-white rounded-3xl shadow-2xl border-0">
            <CardContent className="p-10">
              <h2 className="text-5xl font-bold text-gray-900 mb-8 text-center">Get In Touch</h2>
              
              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-xl text-center">
                  <p className="text-green-700 font-semibold text-lg">
                    ✓ Thanks! We will contact you soon.
                  </p>
                </div>
              )}

              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-lg font-semibold text-gray-800">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="mt-2 border-2 rounded-lg text-lg p-6"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-lg font-semibold text-gray-800">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="mt-2 border-2 rounded-lg text-lg p-6"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-lg font-semibold text-gray-800">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    className="mt-2 border-2 rounded-lg text-lg p-6"
                    rows={5}
                    placeholder="Tell us about your project..."
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Send Message
                </Button>
              </form>

              <div className="mt-10 pt-10 border-t-2 border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center gap-4">
                    <Mail className="w-8 h-8 text-yellow-600" />
                    <div>
                      <p className="font-semibold text-gray-800 text-lg">Email</p>
                      <a href="mailto:jyotiforever84@gmail.com" className="text-gray-700 hover:text-yellow-600 transition-colors">
                        jyotiforever84@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Phone className="w-8 h-8 text-yellow-600" />
                    <div>
                      <p className="font-semibold text-gray-800 text-lg">Phone</p>
                      <a href="tel:+61450900000" className="text-gray-700 hover:text-yellow-600 transition-colors">
                        +61 450 900 000
                      </a>
                    </div>
                  </div>
                </div>
                
                {/* WhatsApp CTA Button */}
                <div className="mt-8 text-center">
                  <a
                    href="whatsapp://send?phone=61450900000&text=Hi I am interested in your services. Please share more details."
                    className="inline-block bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    💬 Chat on WhatsApp Now
                  </a>
                  <p className="text-gray-600 mt-3 text-sm">Get instant response on WhatsApp</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="section-with-grid py-16 px-4 border-t-4 border-white" style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1763615445790-64c644be359b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxwYXN0ZWwlMjBhYnN0cmFjdCUyMGdyYWRpZW50fGVufDB8fHx8MTc3NjE2MDQ1M3ww&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Centered Bold Tagline */}
          <div className="text-center mb-12">
            <p className="text-2xl md:text-3xl font-bold text-gray-900">
              Keeping your brand digitally alive, active, and growing every day.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-10">
            <div>
              <img
                src={logoUrl}
                alt="Jyoti Web Design Logo"
                className="h-20 mb-4 object-contain"
              />
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Jyoti Web Design</h3>
              <p className="text-gray-800 text-lg font-medium leading-relaxed">
                Designing Digital Experiences<br />That Light Up Your Brand
              </p>
            </div>
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-gray-800 hover:text-gray-900 font-medium text-lg transition-colors">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })} className="text-gray-800 hover:text-gray-900 font-medium text-lg transition-colors">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })} className="text-gray-800 hover:text-gray-900 font-medium text-lg transition-colors">
                    Portfolio
                  </button>
                </li>
                <li>
                  <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="text-gray-800 hover:text-gray-900 font-medium text-lg transition-colors">
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Contact Info</h4>
              <ul className="space-y-3">
                <li className="text-gray-800 text-lg">
                  <a href="mailto:jyotiforever84@gmail.com" className="hover:text-gray-900 transition-colors">
                    jyotiforever84@gmail.com
                  </a>
                </li>
                <li className="text-gray-800 text-lg">
                  <a href="tel:+61450900000" className="hover:text-gray-900 transition-colors">
                    +61 450 900 000
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t-2 border-white text-center">
            <p className="text-gray-800 text-lg font-medium">
              © 2025 Jyoti Web Design. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href="whatsapp://send?phone=61450900000&text=Hi I am interested in your services. Please share more details."
        className="fixed bottom-32 right-5 bg-green-500 text-white px-5 py-3 rounded-full shadow-lg hover:bg-green-600 transition z-50"
      >
        💬 WhatsApp
      </a>
    </div>
  );
};

export default Home;
